import { billingLine } from '../../../../models/entities/common_subentities/billingLine';
import { localSDK as client } from '../../../../sdk';
import { buildInvoiceNumber } from '../../businessPartners/singleBusinessPartner/sections/Accounting/invoicePdfTypes';

/**
 * Task 2/3 — Which billing lines go on an invoice
 * ------------------------------------------------
 * - New invoice: all lines minus those already linked in bp_invoice_lines (already sent).
 * - Resend: exact lines from the chosen invoice snapshot (no new ledger / no re-invoicing).
 * - Invoice details column: invoice number per line (— if not yet invoiced).
 */

export type bpInvoiceSummary = {
    id: string;
    invoiceNumber: string;
    invoiceDate: string;
    sentAt: string | null;
    subtotal: number;
    currency: string;
    billingLineIds: number[];
};

export type billingLineInvoiceDetail = {
    invoiceId: string;
    invoiceNumber: string;
    invoiceDate: string;
};

export async function fetchShipmentInvoiceContext(shipmentId: string, businessPartnerId: string) {
    const response = await client.businessPartners().accounting().fetchShipmentInvoices(businessPartnerId, shipmentId);
    return response as { invoices: bpInvoiceSummary[]; uninvoicedCount: number };
}

export async function fetchBillingLineInvoiceDetails(shipmentId: string): Promise<Record<number, billingLineInvoiceDetail>> {
    const raw = await client.shipments().billingLines().fetchInvoiceLineDetails(shipmentId);
    if (!raw || Array.isArray(raw) || typeof raw !== 'object') return {};

    const normalized: Record<number, billingLineInvoiceDetail> = {};
    Object.entries(raw).forEach(([lineId, detail]) => {
        if (detail && typeof detail === 'object' && 'invoiceNumber' in detail) {
            normalized[Number(lineId)] = detail as billingLineInvoiceDetail;
        }
    });
    return normalized;
}

/** Loads line → invoice map from shipment API and BP invoice history (fallback). */
export async function loadInvoiceDetailsByLineId(
    shipmentId: string,
    businessPartnerId?: string | null
): Promise<Record<number, billingLineInvoiceDetail>> {
    let fromDetails: Record<number, billingLineInvoiceDetail> = {};
    try {
        fromDetails = await fetchBillingLineInvoiceDetails(shipmentId);
    } catch {
        /* shipment invoice-line-details may be unavailable */
    }

    if (!businessPartnerId) return fromDetails;

    try {
        const ctx = await fetchShipmentInvoiceContext(shipmentId, businessPartnerId);
        const fromInvoices: Record<number, billingLineInvoiceDetail> = {};
        ctx.invoices.forEach((inv) => {
            inv.billingLineIds.forEach((lineId) => {
                fromInvoices[Number(lineId)] = {
                    invoiceId: inv.id,
                    invoiceNumber: inv.invoiceNumber,
                    invoiceDate: inv.invoiceDate
                };
            });
        });
        return { ...fromDetails, ...fromInvoices };
    } catch {
        return fromDetails;
    }
}

export function formatInvoiceDetailsCell(detail?: billingLineInvoiceDetail | null): string {
    if (!detail?.invoiceNumber) return '—';
    return detail.invoiceNumber;
}

export async function resolveBillingLinesForInvoice(input: {
    shipmentId: string;
    businessPartnerId: string;
    invoiceId?: string | null;
}): Promise<{ billingLines: billingLine[]; invoiceNumber: string; invoiceId?: string; isNew: boolean }> {
    const allLines: billingLine[] = await client.shipments().billingLines().fetchShipmentBillingLines(input.shipmentId);
    const ctx = await fetchShipmentInvoiceContext(input.shipmentId, input.businessPartnerId);

    if (input.invoiceId) {
        const inv = ctx.invoices.find((i) => i.id === input.invoiceId);
        if (!inv) throw new Error('Invoice not found');
        const idSet = new Set(inv.billingLineIds.map((id) => Number(id)));
        return {
            billingLines: allLines.filter((l) => idSet.has(Number(l.id))),
            invoiceNumber: inv.invoiceNumber,
            invoiceId: inv.id,
            isNew: false
        };
    }

    const invoicedIds = new Set<number>();
    ctx.invoices.forEach((inv) => inv.billingLineIds.forEach((id) => invoicedIds.add(Number(id))));
    const uninvoiced = allLines.filter((l) => !invoicedIds.has(Number(l.id)));
    if (!uninvoiced.length) throw new Error('All billing lines are already invoiced. Select a previous invoice to resend or download.');

    const shipment = await client.shipments().fetchById(input.shipmentId);
    const base = buildInvoiceNumber(shipment.CargoZoneNumber);
    const existingNumbers = new Set(ctx.invoices.map((i) => i.invoiceNumber));
    let invoiceNumber = base;
    if (existingNumbers.has(base)) {
        let seq = 2;
        while (existingNumbers.has(`${base}-${seq}`)) seq += 1;
        invoiceNumber = `${base}-${seq}`;
    }

    return { billingLines: uninvoiced, invoiceNumber, isNew: true };
}

export function computeInvoiceAmount(lines: billingLine[]): number {
    return Math.round(lines.reduce((s, l) => s + (Number(l.totalCost) || 0), 0) * 100) / 100;
}
